import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Users, Trophy, HelpCircle, LogOut } from 'lucide-react';
import Monogram from '../brand/Monogram';

/**
 * Top navigation bar - the thin header that frames every non-match screen.
 *
 * Layout (left → right):
 *   [monogram] [MOVIE MAYHEM]   ROSTER / BOX OFFICE / HOW TO PLAY   [sign-in chip]
 *
 * The primary "JOIN BATTLE" CTA lives in the hero, NOT the top bar. The top
 * bar is nav-only chrome so the hero button owns the UX hierarchy without
 * competing against a second, smaller duplicate in the corner.
 *
 * The slash decorators between nav links are drawn via ::after pseudo-elements
 * so they look visually "drawn on" rather than stylesheet-separators - the
 * mass-poster subtitle-card trick applied to navigation.
 */

const Bar = styled.header`
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-6);
  padding: var(--space-4) var(--space-8);
  border-bottom: 1px solid rgba(246, 241, 228, 0.08);
  background: rgba(8, 7, 10, 0.25);
  backdrop-filter: blur(2px);

  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-4);
    gap: var(--space-3);
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  text-decoration: none;
  color: var(--color-cream-100);

  &:hover {
    color: var(--color-cream-100);
    text-decoration: none;
  }
`;

const BrandTitle = styled.span`
  font-family: var(--font-display);
  font-size: var(--size-lg);
  letter-spacing: var(--tracking-wide);
  line-height: 1;
  color: var(--color-cream-100);

  @media (max-width: 640px) {
    display: none;
  }
`;

const NavList = styled.nav`
  display: flex;
  align-items: center;
  gap: var(--space-6);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.button`
  position: relative;
  background: transparent;
  border: none;
  padding: var(--space-2) 0;
  color: var(--color-cream-200);
  font-family: var(--font-heading);
  font-size: var(--size-sm);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color var(--dur-base) var(--ease-out);

  svg {
    stroke-width: 2.5;
  }

  &:hover,
  &:focus-visible {
    color: var(--color-cream-100);
    outline: none;
    text-decoration: none;
  }

  /* amber slash decorator drawn on the right side of each link
     (so it sits between this link and the next one) */
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: -14px;
    top: 50%;
    width: 12px;
    height: 1.5px;
    background: var(--color-amber-500);
    transform: translateY(-50%) rotate(-65deg);
    transform-origin: center;
    opacity: 0.55;
  }
`;

// styled-components v6 removed .withComponent(); the `as` prop on NavLink
// (passed at the JSX site below) is the modern equivalent and preserves
// the same styling rules.

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 140px;
  justify-content: flex-end;
`;

/**
 * Signed-in user identity chip. Quiet by default (ghost border, cream-300),
 * becomes actionable on hover so it's clear it's a sign-out button. Keeping
 * this visible on the top bar (even though the hero CTA is the primary
 * action) is a small but important trust signal - the user can always see
 * which account they're playing as.
 */
const UserChip = styled.button`
  background: transparent;
  border: 1px solid rgba(246, 241, 228, 0.22);
  color: var(--color-cream-200);
  padding: 8px 12px;
  font-family: var(--font-body);
  font-size: var(--size-xs);
  font-weight: var(--weight-medium);
  letter-spacing: var(--tracking-wide);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out),
              background var(--dur-fast) var(--ease-out);

  &:hover,
  &:focus-visible {
    color: var(--color-ink-950);
    background: var(--color-cream-100);
    border-color: var(--color-cream-100);
    outline: none;
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

// A tiny "Sign in" ghost shown only when the user is NOT signed in. Kept
// subdued on purpose: the hero CTA is the primary path into the game.
const GuestHint = styled.span`
  color: var(--color-cream-300);
  font-family: var(--font-body);
  font-size: var(--size-xs);
  font-weight: var(--weight-medium);
  letter-spacing: var(--tracking-wide);
  opacity: 0.8;
  white-space: nowrap;

  @media (max-width: 640px) {
    display: none;
  }
`;

/**
 * @param {object} props
 * @param {() => void} props.onHowToPlay
 * @param {boolean} [props.isSignedIn]
 * @param {string} [props.signedInUsername]
 * @param {() => void} [props.onSignOut]
 */
export const TopBar = ({
    onHowToPlay,
    isSignedIn = false,
    signedInUsername = '',
    onSignOut,
}) => {
    return (
        <Bar>
            <Brand to="/" aria-label="Movie Mayhem home">
                <Monogram size={36} />
                <BrandTitle>MOVIE MAYHEM</BrandTitle>
            </Brand>

            <NavList aria-label="Primary">
                <NavLink as={Link} to="/characters" data-tour="characters-button">
                    <Users size={14} />
                    Fighters
                </NavLink>
                <NavLink as={Link} to="/leaderboard" data-tour="leaderboard-button">
                    <Trophy size={14} />
                    Box Office
                </NavLink>
                <NavLink type="button" onClick={onHowToPlay}>
                    <HelpCircle size={14} />
                    How to Play
                </NavLink>
            </NavList>

            <Right>
                {isSignedIn && signedInUsername && onSignOut ? (
                    <UserChip type="button" onClick={onSignOut} title={`Sign out ${signedInUsername}`}>
                        <LogOut size={12} />
                        {signedInUsername}
                    </UserChip>
                ) : (
                    <GuestHint>Guest</GuestHint>
                )}
            </Right>
        </Bar>
    );
};

export default TopBar;
